import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import {
  getFilters,
} from 'src/slices/filterSlice';
import { useDispatch, useSelector } from 'react-redux';
import { FilterMapper } from 'src/utils/DataMappings';
import { useAuth } from 'src/hooks/useAuth';
import {
  selectClientType,
  selectFilterAccount,
} from 'src/app/selectors/filterSelectors';
import { getDateRange } from 'src/utils/DateUtil';
import {
  selectBrandConfig,
  selectNmsDashConfig,
  selectBrandsAsList,
  selectIsInit,
  selectLocationConfig,
  selectLocationsAsList,
} from 'src/app/selectors/appDataSelectors';
import { useNavigate } from 'react-router-dom';
import { logout } from 'src/app/actions';
import IdleTimer from 'src/utils/IdleTimer';

// Define types for your configuration and clientType
export interface Config {
  API_URL: {
    dash_filter: string;
    dash_interactions: string;
    dash_interactions_time: string;
    dash_transactions: string;
    dash_transactions_sku: string;
    inventory: string;
    inventory_event: string;
    inventory_snapshot: string;
    marketing_survey: string;
    net_ops: string;
    network_availability: string;
    sales_summary: string;
    sessions_summary: string;
    store_status: string;
    transactions: string;
    warehouse_inventory: string;
    nms_workerlogs: string;
    nmsalerts_zendesk: string;
    store_Ids: string;
    location_data: string;
  };
  HEADER: {
    Authorization: string;
  };
}

export interface ConfigContextType {
  config: Config | null;
  loadConfig: () => Promise<void>;
  clientType: string;
  selectedBrands: string[];
  setSelectedBrands: (brands: string[]) => void;
  app: any;
}

const ConfigContext = createContext<ConfigContextType | undefined>(undefined);

export const ConfigProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const dispatch = useDispatch();
  const auth = useAuth();
  const email = auth?.user?.email ?? 'Email not available';
  const { isAuthenticated, user, resetAuth } = useAuth();
  const navigate = useNavigate(); // Use navigate hook to navigate programmatically
  let idleTimer: IdleTimer | null = null;
  // Function to log out the user
  const handleLogout = useCallback(async () => {
    if(idleTimer)
    idleTimer.cleanUp();
    localStorage.clear();
    resetAuth()
    resetConfig()
    await dispatch(logout());
    navigate('/login');
    indexedDB.databases().then((databases) => {
      databases.forEach((db) => {
        indexedDB.deleteDatabase(db.name);
      });
    });   
  }, []);

  useEffect(() => {
    
    if (isAuthenticated && user) {
      // Initialize idle timer
      idleTimer = new IdleTimer({
        timeout: 900, // 15 minutes of inactivity
        onTimeout: handleLogout, // Logout the user on timeout
      });
    }

    // Cleanup the idle timer when the component unmounts or the user is logged out
    return () => {
      if (idleTimer) {
       // idleTimer.cleanUp();
      }
    };
  }, [isAuthenticated, user, handleLogout]); //

  const [config, setConfiglocal] = useState<Config | null>(null);
  const clientType = useSelector(selectClientType);
  const account = useSelector(selectFilterAccount);
  const isInit = useSelector(selectIsInit);
  const loadConfig =
    // clientType === 'Brand'
    //   ? useSelector(selectBrandConfig)
    //   : useSelector(selectLocationConfig);
      clientType === 'Brand'
      ? useSelector(selectBrandConfig)
      : clientType === 'NMS'
        ? useSelector(selectNmsDashConfig)
        : useSelector(selectLocationConfig);

  const [selectedBrands, setSelectedBrands] = useState<string[]>([]); // State for selected brands

  // Use useSelector directly, no need for useMemo here
  const app1 =
    clientType === 'Brand' || clientType === 'NMS'
      ? useSelector(selectBrandsAsList)
      : useSelector(selectLocationsAsList);

  const app =
    app1.length > 0
      ? [...app1].sort((a, b) => {
          if (a.name < b.name) return -1;
          if (a.name > b.name) return 1;
          return 0;
        })
      : [];

  const loadFilters = useCallback(
    async (loadedConfig: Config) => {
      if (!account || account.length === 0) {
        console.error('Account is empty in loadFilters');
        return;
      }
      const defaultrange= (clientType == 'NMS'? getDateRange('last 7 days'): getDateRange('last 30 days'));
      let url = FilterMapper(
        account ?? 'pk',
        loadedConfig,
        email,
        defaultrange.startDate,
        defaultrange.endDate,
        '', //report is empty
        '', // timestamp not required
        '', // filters is empty top filters
      );
      const headers = {
        Authorization: loadedConfig['HEADER']['Authorization'],
      };
      dispatch(getFilters({ url, headers, type: '' }));

      url = FilterMapper(
        account,
        loadedConfig,
        email,
        defaultrange.startDate,
        defaultrange.endDate,
        '', //report is empty
        '', // timestamp not required
        'stores', //filters is empty top filters
      );
      dispatch(getFilters({ url, headers, type: 'stores' }));

      url = FilterMapper(
        account,
        loadedConfig,
        email,
        defaultrange.startDate,
        defaultrange.endDate,
        '', //report is empty
        '', // timestamp not required
        'products', //filters is empty top filters
      );
      dispatch(getFilters({ url, headers, type: 'products' }));
    },
    [dispatch, email, account],
  );

  useEffect(() => {
    if (loadConfig && isInit && account && account?.length > 0) {
      loadFilters(loadConfig);
      setConfiglocal(loadConfig);
    }
  }, [loadConfig, isInit, account, loadFilters]);

  const resetConfig = () => {
    setConfiglocal(null); // Assuming this is how you're storing config in state
    setSelectedBrands([]);
  };

  return (
    <ConfigContext.Provider
      value={{
        config,
        loadConfig,
        clientType,
        selectedBrands,
        setSelectedBrands,
        app,
      }}
    >
      {children}
    </ConfigContext.Provider>
  );
};

// Custom hook for consuming the context
export const useConfig = (): ConfigContextType => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
};
